import React, { useEffect, useState } from 'react';
import { useUser } from '../../context/UserContext';
import { ActivityIcon } from 'lucide-react';
import { trackEvent } from '../../services/AnalyticsService';
interface HumanDesignProfileProps {
  onContinue: () => void;
}
// Human Design types with descriptions
const humanDesignTypes = [{
  type: 'Projector',
  description: 'You thrive by guiding others and waiting for invitations. Your energy is focused and penetrating, allowing you to see deeply into others.'
}, {
  type: 'Generator',
  description: 'You have sustainable life force energy to build and create. Following your gut response leads to satisfaction and fulfillment.'
}, {
  type: 'Manifesting Generator',
  description: "You're quick, multifaceted, and have sustainable energy. You can accomplish many things rapidly when following your gut response."
}, {
  type: 'Manifestor',
  description: "You're an independent initiator with the power to make things happen. Informing others before you act helps create peace in your relationships."
}, {
  type: 'Reflector',
  description: "You're unique and sensitive to your environment. Taking time (a lunar cycle) for decisions helps you find surprise and wonder."
}];
// Human Design authorities with descriptions
const humanDesignAuthorities = [{
  authority: 'Emotional/Solar Plexus',
  description: 'You make your clearest decisions when you wait through the emotional wave. Take time before deciding to reach emotional clarity.',
  decisionGuidance: 'Wait 24-72 hours before making important decisions to allow your emotional wave to complete its cycle.'
}, {
  authority: 'Sacral',
  description: 'Your gut response knows what energizes or drains you. Listen to your spontaneous physical response for guidance.',
  decisionGuidance: 'Ask yourself yes/no questions and notice your immediate gut response. A feeling of energy means yes, while a feeling of depletion means no.'
}, {
  authority: 'Splenic',
  description: 'Your intuition speaks in the moment and then fades. Trust your immediate intuitive hits about safety and wellbeing.',
  decisionGuidance: 'Pay attention to your first instinctual response—it contains wisdom that your mind may later rationalize away.'
}, {
  authority: 'Ego/Heart',
  description: "Your willpower and commitment guide your decisions. If you can commit your energy to something, it's right for you.",
  decisionGuidance: 'Ask yourself: "Can I stand by this decision? Am I willing to commit my energy to this?"'
}, {
  authority: 'G Center/Self',
  description: 'You make decisions from your sense of identity and direction. Listen to your core self for guidance.',
  decisionGuidance: 'Find a quiet space and ask: "Is this truly me? Does this feel aligned with who I am?"'
}, {
  authority: 'Mental Projector/No Authority',
  description: "You don't have consistent access to inner authority. Use your strategy of waiting for invitation and recognition.",
  decisionGuidance: 'Seek input from those you trust, but wait for clear invitations before making significant commitments.'
}, {
  authority: 'Lunar',
  description: 'You need to track decisions through a full lunar cycle before concluding. Your clarity comes through time and reflection.',
  decisionGuidance: 'For important decisions, track how you feel about the choice over 28 days before concluding.'
}];
// More deterministic function to calculate human design type based on birth details
const calculateHumanDesignType = (birthDate: string, birthTime: string, birthLocation: string) => {
  // Parse birth date components
  const dateParts = birthDate.split('-');
  if (dateParts.length !== 3) {
    return humanDesignTypes[0]; // Default to first type if date format is invalid
  }
  const year = parseInt(dateParts[0], 10);
  const month = parseInt(dateParts[1], 10);
  const day = parseInt(dateParts[2], 10);
  // Parse birth time
  let hour = 12; // Default to noon if time parsing fails
  let minute = 0;
  if (birthTime) {
    const timeParts = birthTime.split(':');
    if (timeParts.length === 2) {
      hour = parseInt(timeParts[0], 10);
      minute = parseInt(timeParts[1], 10);
    }
  }
  // Simple algorithm to deterministically assign a type based on birth components
  // This is still a simplified approach but more reliable than the previous hash method
  // Use birth month and day to create a value between 0-365
  const dayOfYear = (month - 1) * 30 + day;
  // Use the hour and minute to create a value between 0-1440 (minutes in a day)
  const minuteOfDay = hour * 60 + minute;
  // Create a combined value using all components
  // Year is used as a multiplier to ensure different years get different distributions
  const combinedValue = (dayOfYear * year + minuteOfDay) % 100;
  // Assign human design type based on the combined value
  // Using a percentage-based distribution:
  // Generators: ~37% (most common)
  // Manifesting Generators: ~33%
  // Projectors: ~20%
  // Manifestors: ~9%
  // Reflectors: ~1% (rarest)
  if (combinedValue < 37) {
    return humanDesignTypes[1]; // Generator
  } else if (combinedValue < 70) {
    return humanDesignTypes[2]; // Manifesting Generator
  } else if (combinedValue < 90) {
    return humanDesignTypes[0]; // Projector
  } else if (combinedValue < 99) {
    return humanDesignTypes[3]; // Manifestor
  } else {
    return humanDesignTypes[4]; // Reflector
  }
};
// Function to calculate authority based on human design type and birth details
const calculateHumanDesignAuthority = (humanDesignType: string, birthDate: string, birthTime: string, birthLocation: string) => {
  // Parse birth date components
  const dateParts = birthDate.split('-');
  if (dateParts.length !== 3) {
    return humanDesignAuthorities[0]; // Default to first authority if date format is invalid
  }
  const year = parseInt(dateParts[0], 10);
  const month = parseInt(dateParts[1], 10);
  const day = parseInt(dateParts[2], 10);
  // Parse birth time
  let hour = 12; // Default to noon if time parsing fails
  let minute = 0;
  if (birthTime) {
    const timeParts = birthTime.split(':');
    if (timeParts.length === 2) {
      hour = parseInt(timeParts[0], 10);
      minute = parseInt(timeParts[1], 10);
    }
  }
  // Create a deterministic value based on birth details
  // Different from the type calculation to ensure variety
  const dayOfYear = (month - 1) * 30 + day;
  const minuteOfDay = hour * 60 + minute;
  const combinedValue = (dayOfYear * year * 13 + minuteOfDay * 7) % 100;
  // Certain types have typical authorities, but there's variety within types
  // This is a simplified model that tries to maintain some type-authority correlations
  // Reflectors always have Lunar authority
  if (humanDesignType === 'Reflector') {
    return humanDesignAuthorities[6]; // Lunar
  }
  // Generators and Manifesting Generators typically have Sacral authority
  if (humanDesignType === 'Generator' || humanDesignType === 'Manifesting Generator') {
    // 80% chance of Sacral, 20% chance of other authorities
    if (combinedValue < 80) {
      return humanDesignAuthorities[1]; // Sacral
    }
  }
  // Manifestors often have Ego/Heart or G Center authority
  if (humanDesignType === 'Manifestor') {
    if (combinedValue < 50) {
      return humanDesignAuthorities[3]; // Ego/Heart
    } else if (combinedValue < 80) {
      return humanDesignAuthorities[4]; // G Center
    }
  }
  // Projectors often have G Center, Mental Projector, or Splenic
  if (humanDesignType === 'Projector') {
    if (combinedValue < 40) {
      return humanDesignAuthorities[4]; // G Center
    } else if (combinedValue < 70) {
      return humanDesignAuthorities[5]; // Mental Projector
    } else if (combinedValue < 90) {
      return humanDesignAuthorities[2]; // Splenic
    }
  }
  // Default distribution for remaining cases
  if (combinedValue < 30) {
    return humanDesignAuthorities[0]; // Emotional/Solar Plexus (most common)
  } else if (combinedValue < 55) {
    return humanDesignAuthorities[1]; // Sacral
  } else if (combinedValue < 75) {
    return humanDesignAuthorities[2]; // Splenic
  } else if (combinedValue < 85) {
    return humanDesignAuthorities[3]; // Ego/Heart
  } else if (combinedValue < 95) {
    return humanDesignAuthorities[4]; // G Center
  } else {
    return humanDesignAuthorities[5]; // Mental Projector
  }
};
const HumanDesignProfile: React.FC<HumanDesignProfileProps> = ({
  onContinue
}) => {
  const {
    userData,
    updateUserData
  } = useUser();
  const [isCalculating, setIsCalculating] = useState(true);
  // Default authority information to ensure fallbacks
  const defaultAuthority = {
    authority: 'Sacral',
    description: 'Your gut response knows what energizes or drains you. Listen to your spontaneous physical response for guidance.',
    decisionGuidance: 'Ask yourself yes/no questions and notice your immediate gut response. A feeling of energy means yes, while a feeling of depletion means no.'
  };
  useEffect(() => {
    // Simulate API call to calculate Human Design
    const timer = setTimeout(() => {
      // If user already has a human design type, use that
      if (userData.humanDesignType) {
        setIsCalculating(false);
        return;
      }
      // Validate that we have all required birth details
      if (!userData.birthDate || !userData.birthTime || !userData.birthLocation) {
        console.error('Missing birth details required for Human Design calculation');
        // Use a default type if missing details
        const defaultType = humanDesignTypes[1]; // Default to Generator
        const defaultAuthority = humanDesignAuthorities[1]; // Default to Sacral
        updateUserData({
          humanDesignType: defaultType.type,
          humanDesignDescription: defaultType.description,
          humanDesignAuthority: defaultAuthority.authority,
          humanDesignAuthorityDescription: defaultAuthority.description,
          humanDesignDecisionGuidance: defaultAuthority.decisionGuidance
        });
        setIsCalculating(false);
        return;
      }
      try {
        // Calculate human design type deterministically based on birth details
        const designType = calculateHumanDesignType(userData.birthDate, userData.birthTime, userData.birthLocation);
        // Calculate authority based on type and birth details
        const designAuthority = calculateHumanDesignAuthority(designType.type, userData.birthDate, userData.birthTime, userData.birthLocation);
        // Track the calculation for analytics
        trackEvent('human_design_calculated', {
          userId: userData.email,
          birthDate: userData.birthDate,
          humanDesignType: designType.type,
          humanDesignAuthority: designAuthority.authority
        });
        updateUserData({
          humanDesignType: designType.type,
          humanDesignDescription: designType.description,
          humanDesignAuthority: designAuthority.authority,
          humanDesignAuthorityDescription: designAuthority.description,
          humanDesignDecisionGuidance: designAuthority.decisionGuidance
        });
      } catch (error) {
        console.error('Error calculating human design:', error);
        // Fallback to default values if calculation fails
        const defaultType = humanDesignTypes[1]; // Default to Generator
        const defaultAuthority = humanDesignAuthorities[1]; // Default to Sacral
        updateUserData({
          humanDesignType: defaultType.type,
          humanDesignDescription: defaultType.description,
          humanDesignAuthority: defaultAuthority.authority,
          humanDesignAuthorityDescription: defaultAuthority.description,
          humanDesignDecisionGuidance: defaultAuthority.decisionGuidance
        });
      } finally {
        setIsCalculating(false);
      }
    }, 2000);
    return () => clearTimeout(timer);
  }, [updateUserData, userData]);
  // Get authority information with fallbacks
  const authorityInfo = {
    authority: userData.humanDesignAuthority || defaultAuthority.authority,
    description: userData.humanDesignAuthorityDescription || defaultAuthority.description,
    guidance: userData.humanDesignDecisionGuidance || defaultAuthority.decisionGuidance
  };
  return <div className="text-center" data-id="element-339">
      <h2 className="text-2xl font-bold mb-6 text-green-700" data-id="element-340">
        Your Human Design
      </h2>
      {isCalculating ? <div className="flex flex-col items-center justify-center py-8" data-id="element-341">
          <ActivityIcon size={40} className="text-green-600 animate-spin mb-4" data-id="element-342" />
          <p className="text-gray-600" data-id="element-343">
            Calculating your Human Design profile...
          </p>
          <p className="text-sm text-gray-500 mt-2" data-id="element-344">
            Using birth details: {userData.birthDate || 'Not provided'},{' '}
            {userData.birthTime || 'Not provided'},{' '}
            {userData.birthLocation || 'Not provided'}
          </p>
        </div> : <>
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-4" data-id="element-345">
            <h3 className="text-xl font-bold text-green-800 mb-2" data-id="element-346">
              {userData.humanDesignType || 'Generator'}
            </h3>
            <p className="text-gray-700" data-id="element-347">
              {userData.humanDesignDescription || 'You have sustainable life force energy to build and create. Following your gut response leads to satisfaction and fulfillment.'}
            </p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6" data-id="element-348">
            <h3 className="text-xl font-bold text-blue-800 mb-2" data-id="element-349">
              Your Authority: {authorityInfo.authority}
            </h3>
            <p className="text-gray-700 mb-3" data-id="element-350">{authorityInfo.description}</p>
            <div className="bg-white bg-opacity-80 p-3 rounded-md" data-id="element-351">
              <h4 className="font-medium text-blue-700 mb-1" data-id="element-352">
                Decision Guidance:
              </h4>
              <p className="text-gray-700 text-sm" data-id="element-353">{authorityInfo.guidance}</p>
            </div>
          </div>
          <button onClick={onContinue} className="bg-green-600 text-white py-3 px-6 rounded-lg shadow-md hover:bg-green-700 transition-colors w-full font-medium" data-id="element-354">
            Continue
          </button>
        </>}
    </div>;
};
export default HumanDesignProfile;